/**
 * A QR code encoder with no dependency and no network: byte mode, error
 * correction level M, versions 1-40, penalty-scored mask selection (ISO/IEC
 * 18004). It follows the structure of Project Nayuki's reference encoder.
 *
 * The API renders QR images with `segno` at the same level and the same
 * four-module quiet zone (`campaigns/qr.py`), so a preview drawn here scans the
 * same way the served image does. The modules are not always identical — segno
 * pads the data differently — but both decode to the same text. It is also what
 * the demo client downloads, so a QR code in the demo never reaches any service.
 */

/** Level M, per version (index 0 unused). */
const ECC_CODEWORDS_PER_BLOCK = [
  -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28,
  28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
]
const ERROR_CORRECTION_BLOCKS = [
  -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33,
  35, 37, 38, 40, 43, 45, 47, 49,
]
/** Level M's two format bits. */
const FORMAT_BITS_M = 0

const MIN_VERSION = 1
const MAX_VERSION = 40

export interface QrCode {
  version: number
  size: number
  mask: number
  /** `modules[y][x]`, true for a dark module. */
  modules: boolean[][]
}

function bit(value: number, index: number): boolean {
  return ((value >>> index) & 1) !== 0
}

function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64
  if (version >= 2) {
    const alignments = Math.floor(version / 7) + 2
    result -= (25 * alignments - 10) * alignments - 55
    if (version >= 7) result -= 36
  }
  return result
}

function dataCodewords(version: number): number {
  return (
    Math.floor(rawDataModules(version) / 8) -
    (ECC_CODEWORDS_PER_BLOCK[version] as number) * (ERROR_CORRECTION_BLOCKS[version] as number)
  )
}

// --- Reed-Solomon over GF(2^8) / 0x11D ----------------------------------------

function multiply(x: number, y: number): number {
  let z = 0
  for (let i = 7; i >= 0; i -= 1) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z & 0xff
}

function divisor(degree: number): number[] {
  const result = new Array<number>(degree).fill(0)
  result[degree - 1] = 1
  let root = 1
  for (let i = 0; i < degree; i += 1) {
    for (let j = 0; j < result.length; j += 1) {
      result[j] = multiply(result[j] as number, root)
      if (j + 1 < result.length) result[j] = (result[j] as number) ^ (result[j + 1] as number)
    }
    root = multiply(root, 0x02)
  }
  return result
}

function remainder(data: readonly number[], generator: readonly number[]): number[] {
  const result = generator.map(() => 0)
  for (const value of data) {
    const factor = value ^ (result.shift() as number)
    result.push(0)
    generator.forEach((coefficient, index) => {
      result[index] = (result[index] as number) ^ multiply(coefficient, factor)
    })
  }
  return result
}

function withErrorCorrection(data: readonly number[], version: number): number[] {
  const blocks = ERROR_CORRECTION_BLOCKS[version] as number
  const eccLength = ECC_CODEWORDS_PER_BLOCK[version] as number
  const rawCodewords = Math.floor(rawDataModules(version) / 8)
  const shortBlocks = blocks - (rawCodewords % blocks)
  const shortLength = Math.floor(rawCodewords / blocks)
  const generator = divisor(eccLength)

  const all: number[][] = []
  let offset = 0
  for (let index = 0; index < blocks; index += 1) {
    const length = shortLength - eccLength + (index < shortBlocks ? 0 : 1)
    const block = data.slice(offset, offset + length)
    offset += length
    const ecc = remainder(block, generator)
    // A placeholder keeps short and long blocks aligned for interleaving; it is skipped below.
    all.push([...block, ...(index < shortBlocks ? [0] : []), ...ecc])
  }

  const result: number[] = []
  for (let i = 0; i < (all[0] as number[]).length; i += 1) {
    all.forEach((block, j) => {
      // Short blocks have no codeword at the last data position.
      if (i !== shortLength - eccLength || j >= shortBlocks) result.push(block[i] as number)
    })
  }
  return result
}

// --- the symbol ---------------------------------------------------------------

class QrSymbol {
  readonly size: number
  readonly modules: boolean[][]
  private readonly reserved: boolean[][]

  constructor(readonly version: number) {
    this.size = version * 4 + 17
    this.modules = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false))
    this.reserved = Array.from({ length: this.size }, () => new Array<boolean>(this.size).fill(false))
  }

  private set(x: number, y: number, dark: boolean): void {
    ;(this.modules[y] as boolean[])[x] = dark
    ;(this.reserved[y] as boolean[])[x] = true
  }

  alignmentPositions(): number[] {
    if (this.version === 1) return []
    const count = Math.floor(this.version / 7) + 2
    const step = Math.floor((this.version * 8 + count * 3 + 5) / (count * 4 - 4)) * 2
    const result = [6]
    for (let position = this.size - 7; result.length < count; position -= step) result.splice(1, 0, position)
    return result
  }

  drawFunctionPatterns(): void {
    for (let i = 0; i < this.size; i += 1) {
      this.set(6, i, i % 2 === 0)
      this.set(i, 6, i % 2 === 0)
    }
    for (const [x, y] of [
      [3, 3],
      [this.size - 4, 3],
      [3, this.size - 4],
    ] as const) {
      for (let dy = -4; dy <= 4; dy += 1) {
        for (let dx = -4; dx <= 4; dx += 1) {
          const distance = Math.max(Math.abs(dx), Math.abs(dy))
          const xx = x + dx
          const yy = y + dy
          if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.set(xx, yy, distance !== 2 && distance !== 4)
        }
      }
    }
    const positions = this.alignmentPositions()
    const last = positions.length - 1
    positions.forEach((y, i) => {
      positions.forEach((x, j) => {
        // The three corners hold finder patterns.
        if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) return
        for (let dy = -2; dy <= 2; dy += 1) {
          for (let dx = -2; dx <= 2; dx += 1) this.set(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
        }
      })
    })
    this.drawFormatBits(0)
    this.drawVersion()
  }

  drawFormatBits(mask: number): void {
    const data = (FORMAT_BITS_M << 3) | mask
    let rem = data
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
    const bits = ((data << 10) | rem) ^ 0x5412

    for (let i = 0; i <= 5; i += 1) this.set(8, i, bit(bits, i))
    this.set(8, 7, bit(bits, 6))
    this.set(8, 8, bit(bits, 7))
    this.set(7, 8, bit(bits, 8))
    for (let i = 9; i < 15; i += 1) this.set(14 - i, 8, bit(bits, i))
    for (let i = 0; i < 8; i += 1) this.set(this.size - 1 - i, 8, bit(bits, i))
    for (let i = 8; i < 15; i += 1) this.set(8, this.size - 15 + i, bit(bits, i))
    this.set(8, this.size - 8, true)
  }

  private drawVersion(): void {
    if (this.version < 7) return
    let rem = this.version
    for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
    const bits = (this.version << 12) | rem
    for (let i = 0; i < 18; i += 1) {
      const a = this.size - 11 + (i % 3)
      const b = Math.floor(i / 3)
      this.set(a, b, bit(bits, i))
      this.set(b, a, bit(bits, i))
    }
  }

  drawCodewords(codewords: readonly number[]): void {
    let index = 0
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5
      for (let vertical = 0; vertical < this.size; vertical += 1) {
        for (let j = 0; j < 2; j += 1) {
          const x = right - j
          const upward = ((right + 1) & 2) === 0
          const y = upward ? this.size - 1 - vertical : vertical
          if (!(this.reserved[y] as boolean[])[x] && index < codewords.length * 8) {
            ;(this.modules[y] as boolean[])[x] = bit(codewords[index >>> 3] as number, 7 - (index & 7))
            index += 1
          }
        }
      }
    }
  }

  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        let invert: boolean
        switch (mask) {
          case 0: invert = (x + y) % 2 === 0; break
          case 1: invert = y % 2 === 0; break
          case 2: invert = x % 3 === 0; break
          case 3: invert = (x + y) % 3 === 0; break
          case 4: invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0; break
          case 5: invert = ((x * y) % 2) + ((x * y) % 3) === 0; break
          case 6: invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0; break
          default: invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0; break
        }
        const row = this.modules[y] as boolean[]
        if (!(this.reserved[y] as boolean[])[x] && invert) row[x] = !row[x]
      }
    }
  }

  penalty(): number {
    let result = 0
    const size = this.size
    const at = (x: number, y: number) => (this.modules[y] as boolean[])[x] as boolean

    const addHistory = (length: number, history: number[]) => {
      if (history[0] === 0) length += size
      history.pop()
      history.unshift(length)
    }
    const countPatterns = (history: readonly number[]) => {
      const n = history[1] as number
      const core =
        n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n
      return (
        (core && (history[0] as number) >= n * 4 && (history[6] as number) >= n ? 1 : 0) +
        (core && (history[6] as number) >= n * 4 && (history[0] as number) >= n ? 1 : 0)
      )
    }
    const terminate = (color: boolean, length: number, history: number[]) => {
      if (color) {
        addHistory(length, history)
        length = 0
      }
      addHistory(length + size, history)
      return countPatterns(history)
    }
    const scan = (read: (i: number, j: number) => boolean) => {
      for (let i = 0; i < size; i += 1) {
        let color = false
        let run = 0
        const history = [0, 0, 0, 0, 0, 0, 0]
        for (let j = 0; j < size; j += 1) {
          if (read(i, j) === color) {
            run += 1
            if (run === 5) result += 3
            else if (run > 5) result += 1
          } else {
            addHistory(run, history)
            if (!color) result += countPatterns(history) * 40
            color = read(i, j)
            run = 1
          }
        }
        result += terminate(color, run, history) * 40
      }
    }
    scan((y, x) => at(x, y))
    scan((x, y) => at(x, y))

    for (let y = 0; y < size - 1; y += 1) {
      for (let x = 0; x < size - 1; x += 1) {
        const color = at(x, y)
        if (color === at(x + 1, y) && color === at(x, y + 1) && color === at(x + 1, y + 1)) result += 3
      }
    }

    let dark = 0
    for (const row of this.modules) for (const module of row) if (module) dark += 1
    const total = size * size
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1
    return result + k * 10
  }
}

function utf8(text: string): number[] {
  return [...new TextEncoder().encode(text)]
}

/**
 * Encodes `text` as the smallest QR code that holds it at level M. `mask`
 * forces a mask pattern (0-7); by default the lowest-penalty one is chosen.
 */
export function encodeQr(text: string, options: { mask?: number } = {}): QrCode {
  const bytes = utf8(text)
  let version = MIN_VERSION
  for (; ; version += 1) {
    const countBits = version <= 9 ? 8 : 16
    if (4 + countBits + bytes.length * 8 <= dataCodewords(version) * 8) break
    if (version >= MAX_VERSION) throw new RangeError('text is too long for a QR code')
  }

  const bits: number[] = []
  const append = (value: number, length: number) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1)
  }
  append(0b0100, 4)
  append(bytes.length, version <= 9 ? 8 : 16)
  for (const value of bytes) append(value, 8)
  const capacity = dataCodewords(version) * 8
  append(0, Math.min(4, capacity - bits.length))
  append(0, (8 - (bits.length % 8)) % 8)
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) append(pad, 8)

  const data: number[] = []
  for (let i = 0; i < bits.length; i += 8) data.push(parseInt(bits.slice(i, i + 8).join(''), 2))

  const symbol = new QrSymbol(version)
  symbol.drawFunctionPatterns()
  symbol.drawCodewords(withErrorCorrection(data, version))

  let mask = options.mask
  if (mask === undefined) {
    let best = Number.POSITIVE_INFINITY
    for (let candidate = 0; candidate < 8; candidate += 1) {
      symbol.applyMask(candidate)
      symbol.drawFormatBits(candidate)
      const penalty = symbol.penalty()
      if (penalty < best) {
        best = penalty
        mask = candidate
      }
      symbol.applyMask(candidate)
    }
  }
  const chosen = mask ?? 0
  symbol.applyMask(chosen)
  symbol.drawFormatBits(chosen)
  return { version, size: symbol.size, mask: chosen, modules: symbol.modules }
}

/** The quiet zone the specification requires, in modules. */
export const QR_BORDER = 4

/** An SVG document for `text`: black modules on white, one path, crisp at any scale. */
export function qrSvg(text: string, { title }: { title?: string } = {}): string {
  const code = encodeQr(text)
  const extent = code.size + QR_BORDER * 2
  let path = ''
  code.modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${x + QR_BORDER} ${y + QR_BORDER}h1v1h-1z`
    })
  })
  const label = title ? `<title>${escapeXml(title)}</title>` : ''
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${extent} ${extent}" width="${extent * 10}" height="${extent * 10}" shape-rendering="crispEdges" role="img">` +
    `${label}<rect width="${extent}" height="${extent}" fill="#ffffff"/><path d="${path}" fill="#000000"/></svg>`
  )
}

function escapeXml(value: string): string {
  return value.replace(/[<>&"']/g, (char) => `&#${char.charCodeAt(0)};`)
}
